import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Check } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import { Reveal } from "../components/Motion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { IMAGES, ENQUIRY_TYPES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", organisation: "", phone: "",
    enquiry_type: params.get("enquiry") || "General",
    subject: params.get("subject") || "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Thank you — your enquiry is on its way.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title="Contact"
      description="Work with Ranger Base. Get in touch to build a strong, self-sustaining ranger program for your organisation. Supporting Indigenous ranger programs around the world."
    >
      {/* Aerial hero */}
      <section className="relative h-[56vh] min-h-[440px] w-full overflow-hidden bg-eucalyptus-deep">
        <motion.img
          initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          src={IMAGES.fireGrass} alt="Cultural burn on Country" className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-eucalyptus-deep/90 via-eucalyptus-deep/30 to-eucalyptus-deep/40" />
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative h-full mx-auto max-w-[88rem] px-6 lg:px-10 flex items-end pb-14">
          <div>
            <div className="text-sandstone mb-5"><Overline>Work With Us</Overline></div>
            <h1 className="font-heading text-warmwhite text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95]">Let's build it together.</h1>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left info + map */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
                Tell us about your Country and your goals.
              </h2>
              <p className="mt-5 text-lg text-charcoal/70 leading-relaxed">
                Whether you are starting a ranger program or strengthening an established one, we would
                love to hear from you. We work alongside communities around the
                world.
              </p>
            </Reveal>

            <div className="mt-8 space-y-4">
              <a href="mailto:hello@rangerbase.com" data-testid="contact-email" className="flex items-center gap-4 rounded-2xl border border-charcoal/10 bg-white p-5 hover:border-eucalyptus/40 transition-colors">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-eucalyptus/10 text-eucalyptus"><Mail size={18} /></span>
                <div><div className="text-xs tracking-overline uppercase text-earth">Email</div><div className="font-medium text-charcoal">hello@rangerbase.com</div></div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-charcoal/10 bg-white p-5">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-eucalyptus/10 text-eucalyptus"><MapPin size={18} /></span>
                <div><div className="text-xs tracking-overline uppercase text-earth">Service area</div><div className="font-medium text-charcoal">Global · Working worldwide</div></div>
              </div>
            </div>

            {/* Logo panel */}
            <div className="mt-6 rounded-3xl overflow-hidden border border-charcoal/10 bg-warmwhite relative aspect-[4/3] flex items-center justify-center p-10">
              <img src="/rangerbase-logo.png" alt="Ranger Base" className="max-h-full max-w-[80%] object-contain" data-testid="contact-logo" />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            {done ? (
              <Reveal>
                <div data-testid="contact-success" className="rounded-[2rem] bg-eucalyptus text-warmwhite p-10 lg:p-14 relative overflow-hidden">
                  <div className="grain absolute inset-0 opacity-30" />
                  <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-warmwhite/15 mb-6"><Check size={28} /></span>
                  <h3 className="relative font-heading text-3xl md:text-4xl font-semibold tracking-tight">Thank you, {form.name.split(" ")[0] || "friend"}.</h3>
                  <p className="relative mt-4 text-warmwhite/80 text-lg leading-relaxed max-w-md">Your enquiry has reached our team and a copy is on its way to us. We will be in touch soon. Walking together, for Country.</p>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="rounded-[2rem] border border-charcoal/10 bg-white p-8 lg:p-10">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name *"><input data-testid="contact-name" value={form.name} onChange={update("name")} required className="cf-input" placeholder="Your name" /></Field>
                  <Field label="Email *"><input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} required className="cf-input" placeholder="you@example.com" /></Field>
                  <Field label="Organisation"><input data-testid="contact-org" value={form.organisation} onChange={update("organisation")} className="cf-input" placeholder="Corporation / group" /></Field>
                  <Field label="Phone"><input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="cf-input" placeholder="Optional" /></Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <Field label="Enquiry type">
                    <Select value={form.enquiry_type} onValueChange={(v) => setForm((f) => ({ ...f, enquiry_type: v }))}>
                      <SelectTrigger data-testid="contact-enquiry-type" className="cf-input h-auto py-3 text-left"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ENQUIRY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`enquiry-opt-${t.toLowerCase().replace(/\s|&/g, "-")}`}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Subject"><input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="cf-input" placeholder="Brief subject" /></Field>
                </div>

                <div className="mt-5">
                  <Field label="Message *">
                    <textarea data-testid="contact-message" value={form.message} onChange={update("message")} required rows={5} className="cf-input resize-none" placeholder="Tell us about your organisation, your Country and what you would like to build." />
                  </Field>
                </div>

                <button type="submit" disabled={busy} data-testid="contact-submit" className="mt-6 inline-flex items-center gap-2 rounded-full bg-eucalyptus text-warmwhite px-8 py-4 font-semibold hover:bg-eucalyptus-deep transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Send enquiry"} <ArrowUpRight size={18} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .cf-input { width:100%; border:1px solid rgba(26,26,26,0.15); background:#F7F5F0; border-radius:1rem; padding:0.75rem 1rem; color:#1A1A1A; transition:border-color .3s; }
        .cf-input::placeholder { color:rgba(26,26,26,0.4); }
        .cf-input:focus { outline:none; border-color:#2C4A3B; }
      `}</style>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-overline uppercase text-earth block mb-2">{label}</span>
    {children}
  </label>
);
